import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  Divider,
  Tabs,
  Tab,
} from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts'
import {
  Assessment as AnalyticsIcon,
  CheckCircle as CompletedIcon,
  AccessTime as InProgressIcon,
  Error as ErrorIcon,
  Speed as AvgTimeIcon,
  Warning as StaleIcon,
  TrendingUp as PerformanceIcon,
  PieChart as DistributionIcon,
  Schedule as RecentIcon,
} from '@mui/icons-material'
import { analyticsAPI } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import AnalyticsFilters from '../components/AnalyticsFilters'

const AnalyticsDashboard = () => {
  const theme = useTheme()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  
  // Data states
  const [ticketCounts, setTicketCounts] = useState(null)
  const [avgCompletionTime, setAvgCompletionTime] = useState(null)
  const [staleTickets, setStaleTickets] = useState({ tickets: [], pagination: null })
  const [agentPerformance, setAgentPerformance] = useState({ agents: [] })
  const [ticketDistribution, setTicketDistribution] = useState(null)
  const [recentActivity, setRecentActivity] = useState({ tickets: [] })
  const [availableFilters, setAvailableFilters] = useState({ ticket_types: [], created_by_users: [] })
  
  // Filter states
  const [filters, setFilters] = useState({
    ticket_type: '',
    created_by: '',
    date_from: '',
    date_to: '',
  })
  
  // Pagination states
  const [stalePage, setStalePage] = useState(0)
  const [stalePageSize, setStalePageSize] = useState(10)

  // Check if user is admin (from token OR application roles)
  const hasTokenAdminRole = user?.roles?.some(role =>
    role.toLowerCase() === 'agentflow.admin'
  )
  const hasApplicationAdminRole = user?.application_roles?.some(role =>
    role.toLowerCase() === 'application.admin'
  )
  const isAdmin = hasTokenAdminRole || hasApplicationAdminRole

  // Load available filters on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const filtersData = await analyticsAPI.getAvailableFilters()
        setAvailableFilters(filtersData)
      } catch (err) {
        console.error('Error loading filters:', err)
      }
    }
    loadFilters()
  }, [])

  // Load data when filters change
  useEffect(() => {
    loadAllData()
  }, [filters])

  const loadAllData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Convert date strings to proper ISO format if they exist
      const apiFilters = {
        ...filters,
        date_from: filters.date_from ? new Date(filters.date_from).toISOString() : '',
        date_to: filters.date_to ? new Date(filters.date_to).toISOString() : '',
      }
      
      // Remove empty filters
      const cleanFilters = Object.fromEntries(
        Object.entries(apiFilters).filter(([_, value]) => value !== '' && value !== null)
      )

      // Load all data concurrently
      const [
        countsData,
        avgTimeData,
        staleData,
        performanceData,
        distributionData,
        activityData,
      ] = await Promise.all([
        analyticsAPI.getTicketCounts(cleanFilters),
        analyticsAPI.getAverageCompletionTime(cleanFilters),
        analyticsAPI.getStaleTickets(cleanFilters, stalePage + 1, stalePageSize),
        analyticsAPI.getAgentPerformance(cleanFilters),
        analyticsAPI.getTicketDistribution(cleanFilters),
        analyticsAPI.getRecentActivity(24, 20),
      ])

      setTicketCounts(countsData)
      setAvgCompletionTime(avgTimeData)
      setStaleTickets(staleData)
      setAgentPerformance(performanceData)
      setTicketDistribution(distributionData)
      setRecentActivity(activityData)
      
    } catch (err) {
      console.error('Error loading analytics data:', err)
      setError(`Failed to load analytics data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setStalePage(0) // Reset pagination when filters change
  }

  const handleStalePageChange = (event, newPage) => {
    setStalePage(newPage)
  }

  const handleStalePageSizeChange = (event) => {
    setStalePageSize(parseInt(event.target.value, 10))
    setStalePage(0)
  }

  // Load stale tickets when pagination changes
  useEffect(() => {
    if (!loading && staleTickets.pagination) {
      loadStaleTicketsPage()
    }
  }, [stalePage, stalePageSize])

  const loadStaleTicketsPage = async () => {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
      )
      const staleData = await analyticsAPI.getStaleTickets(cleanFilters, stalePage + 1, stalePageSize)
      setStaleTickets(staleData)
    } catch (err) {
      console.error('Error loading stale tickets page:', err)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Color schemes for charts
  const statusColors = {
    closed: theme.palette.success.main,
    in_progress: theme.palette.warning.main,
    error: theme.palette.error.main,
    total: theme.palette.primary.main,
  }

  const pieColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ]

  if (loading && !ticketCounts) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AnalyticsIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isAdmin ? 'Comprehensive ticket analytics and insights' : 'Your ticket analytics and insights'}
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <AnalyticsFilters
          filters={filters}
          availableFilters={availableFilters}
          onFilterChange={handleFilterChange}
          isAdmin={isAdmin}
        />
      </Paper>

      {/* Status Cards */}
      {ticketCounts && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Completed
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {ticketCounts.closed}
                    </Typography>
                  </Box>
                  <CompletedIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      In Progress
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {ticketCounts.in_progress}
                    </Typography>
                  </Box>
                  <InProgressIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Error
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {ticketCounts.error}
                    </Typography>
                  </Box>
                  <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {ticketCounts.total}
                    </Typography>
                  </Box>
                  <AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Average Completion Time */}
      {avgCompletionTime && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="text.secondary" gutterBottom variant="h6">
                  Average Completion Time
                </Typography>
                <Typography variant="h4" color="info.main">
                  {avgCompletionTime.average_completion_time_human || 'No data'}
                </Typography>
              </Box>
              <AvgTimeIcon sx={{ fontSize: 40, color: 'info.main' }} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabbed Content */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Stale Tickets" icon={<StaleIcon />} />
          <Tab label="Agent Performance" icon={<PerformanceIcon />} />
          <Tab label="Distribution" icon={<DistributionIcon />} />
          <Tab label="Recent Activity" icon={<RecentIcon />} />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Stale Tickets Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Overdue Tickets
              </Typography>
              {staleTickets.tickets.length > 0 ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Ticket ID</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Assigned To</TableCell>
                          <TableCell>Age</TableCell>
                          <TableCell>Overdue By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staleTickets.tickets.map((item) => (
                          <TableRow key={item.ticket_id}>
                            <TableCell>{item.ticket_id}</TableCell>
                            <TableCell>
                              <Chip label={item.ticket_type} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.status} 
                                size="small" 
                                color="warning"
                              />
                            </TableCell>
                            <TableCell>{item.assigned_to || 'Unassigned'}</TableCell>
                            <TableCell>{item.age_human}</TableCell>
                            <TableCell>
                              <Typography color="error.main" variant="body2">
                                +{item.exceeded_by_human}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {staleTickets.pagination && (
                    <TablePagination
                      component="div"
                      count={staleTickets.pagination.total_items}
                      page={stalePage}
                      onPageChange={handleStalePageChange}
                      rowsPerPage={stalePageSize}
                      onRowsPerPageChange={handleStalePageSizeChange}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                  )}
                </>
              ) : (
                <Alert severity="success">No overdue tickets found!</Alert>
              )}
            </Box>
          )}

          {/* Agent Performance Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Agent Performance
              </Typography>
              {agentPerformance.agents.length > 0 ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Agent</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Completed</TableCell>
                          <TableCell align="right">Error</TableCell>
                          <TableCell align="right">Success Rate</TableCell>
                          <TableCell align="right">Avg Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agentPerformance.agents.map((agent) => (
                          <TableRow key={agent.agent_name}>
                            <TableCell>{agent.agent_name}</TableCell>
                            <TableCell align="right">{agent.total_tickets}</TableCell>
                            <TableCell align="right">{agent.closed_tickets}</TableCell>
                            <TableCell align="right">{agent.error_tickets}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={`${agent.success_rate.toFixed(1)}%`}
                                color={agent.success_rate >= 90 ? 'success' : agent.success_rate >= 70 ? 'warning' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {agent.average_completion_time_human || 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Performance Chart */}
                  <Box sx={{ mt: 3, height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={agentPerformance.agents}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="agent_name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total_tickets" fill={theme.palette.primary.main} name="Total" />
                        <Bar dataKey="closed_tickets" fill={theme.palette.success.main} name="Completed" />
                        <Bar dataKey="error_tickets" fill={theme.palette.error.main} name="Error" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              ) : (
                <Alert severity="info">No agent performance data available</Alert>
              )}
            </Box>
          )}

          {/* Distribution Tab */}
          {activeTab === 2 && ticketDistribution && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Ticket Distribution
              </Typography>
              <Grid container spacing={3}>
                {/* By Type */}
                {Object.keys(ticketDistribution.by_type).length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        By Type
                      </Typography>
                      <Box sx={{ height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(ticketDistribution.by_type).map(([type, count]) => ({
                                name: type,
                                value: count
                              }))}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label
                            >
                              {Object.keys(ticketDistribution.by_type).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* By Status */}
                {Object.keys(ticketDistribution.by_status).length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        By Status
                      </Typography>
                      <Box sx={{ height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(ticketDistribution.by_status).map(([status, count]) => ({
                                name: status,
                                value: count
                              }))}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label
                            >
                              {Object.keys(ticketDistribution.by_status).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Recent Activity Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Recent Activity (Last 24 Hours)
              </Typography>
              {recentActivity.tickets.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticket ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Last Modified</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentActivity.tickets.map((ticket) => (
                        <TableRow key={ticket.ticket_id}>
                          <TableCell>{ticket.ticket_id}</TableCell>
                          <TableCell>
                            <Chip label={ticket.ticket_type} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={ticket.status} 
                              size="small"
                              color={
                                ticket.status === 'complete' ? 'success' :
                                ticket.status === 'error' ? 'error' :
                                'warning'
                              }
                            />
                          </TableCell>
                          <TableCell>{ticket.assigned_to || 'Unassigned'}</TableCell>
                          <TableCell>{ticket.time_ago_human}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No recent activity in the last 24 hours</Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default AnalyticsDashboard